import { useMemo } from "react";
import type { GridBlock } from "@juro/shared";

type RowValues = Record<string, Record<string, string | number | boolean | null>>;

function toNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string") return Number(value) || 0;
  return 0;
}

function readPath(ctx: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let value: unknown = ctx;
  for (const part of parts) {
    if (value && typeof value === "object" && part in value) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return value;
}

function isDescendant(rowId: string, ancestorId: string, parents: Record<string, string | undefined>): boolean {
  let current: string | undefined = rowId;
  while (current) {
    if (current === ancestorId) return true;
    current = parents[current];
  }
  return false;
}

function evalFormula(raw: string, ctx: Record<string, unknown>, rows: RowValues, parents: Record<string, string | undefined>, types: Record<string, string | undefined>): number {
  let expr = raw.trim().replace(/^=/, "");
  expr = expr.replace(/sum\(rows\[([^\]]+)\]\.([A-Za-z_]\w*)\)/g, (_m, selector: string, field: string) => {
    const sum = Object.entries(rows).reduce((total, [id, row]) => {
      const matches =
        selector === "*" ||
        (selector.endsWith(".*") && isDescendant(id, selector.slice(0, -2), parents)) ||
        (selector === "*.subtotal" && types[id] === "subtotal") ||
        id === selector;
      return matches ? total + toNumber(row[field]) : total;
    }, 0);
    return String(sum);
  });
  expr = expr.replace(/withholding\(([^)]+)\)/g, (_m, inner: string) => {
    const amount = evalFormula(`=${inner}`, ctx, rows, parents, types);
    return String(Math.floor(Math.max(amount - 10000, 0) * 0.1021));
  });
  expr = expr.replace(/consumption_tax\(([^),]+)(?:,\s*([^)]+))?\)/g, (_m, amountExpr: string, rateExpr: string | undefined) => {
    const amount = evalFormula(`=${amountExpr}`, ctx, rows, parents, types);
    const rate = rateExpr ? evalFormula(`=${rateExpr}`, ctx, rows, parents, types) : 0.1;
    return String(Math.floor(amount * rate));
  });
  expr = expr.replace(/\{([^}]+)\}/g, (_m, path: string) => String(toNumber(readPath(ctx, path.trim()))));
  if (!/^[\d+\-*/().\s]+$/.test(expr)) return 0;
  return Function(`"use strict"; return (${expr});`)() as number;
}

export function evaluateGridValues(block: GridBlock): RowValues {
  const rows: RowValues = {};
  const parents: Record<string, string | undefined> = {};
  const types: Record<string, string | undefined> = {};
  for (const row of block.rows) {
    rows[row.id] = { id: row.id, label: row.label ?? "", ...(row.cells ?? {}) };
    parents[row.id] = row.parent;
    types[row.id] = row.type;
  }

  for (let pass = 0; pass < 6; pass += 1) {
    for (const row of block.rows) {
      const current = rows[row.id];
      if (!current) continue;
      const ctx = { row: current, rows };
      for (const column of block.columns) {
        const raw = current[column.id] ?? column.formula;
        if (typeof raw === "string" && raw.trim().startsWith("=")) {
          current[column.id] = evalFormula(raw, ctx, rows, parents, types);
        }
      }
      for (const [key, raw] of Object.entries(row.cells ?? {})) {
        if (typeof raw === "string" && raw.trim().startsWith("=")) {
          current[key] = evalFormula(raw, ctx, rows, parents, types);
        }
      }
    }
  }
  return rows;
}

export function useEvaluatedGrid(block: GridBlock): RowValues {
  return useMemo(() => evaluateGridValues(block), [block]);
}
